/* eslint-disable no-undef */
import { Navbar, TesSearch, Input } from "components";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LogoLoader from "components/widgets/loader/LogoLoader";
import { ISchools } from "types";
import Select, { SingleValue } from "react-select";
import { zones } from "pages/updateProfile/DropDownOptions";
import { useFetchSchoolsQuery, useUpdateSchoolMutation } from "services/schoolApi";

const ITEMS_PER_PAGE = 9;

interface SelectOption {
  value: string;
  label: string;
}

interface SchoolFormData {
  nameOfSchool: string;
  category: string;
  location: string;
  zone: string;
  division: string;
  address?: string;
}

export const UpdateSchoolView: React.FC = () => {
  const { data: schoolsResponse, isLoading, isError } = useFetchSchoolsQuery();
  const [updateSchool] = useUpdateSchoolMutation();

  const schools = (schoolsResponse ?? []) as ISchools[];

  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [openSchoolId, setOpenSchoolId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showPrevNext, setShowPrevNext] = useState<boolean>(false);
  const [currentSchoolIndex, setCurrentSchoolIndex] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Form state for each school
  const [formData, setFormData] = useState<{ [key: string]: SchoolFormData }>({});
  const [isEditing, setIsEditing] = useState<{ [key: string]: boolean }>({});
  const [isSaving, setIsSaving] = useState<{ [key: string]: boolean }>({});
  const [updateSuccess, setUpdateSuccess] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    setCurrentPage(1);
    setOpenSchoolId(null);
    setShowPrevNext(false);
    setCurrentSchoolIndex(0);
  }, [selectedZone]);

  // Initialize form data when a school is opened
  useEffect(() => {
    if (openSchoolId) {
      const school = schools.find((s) => s._id === openSchoolId);
      if (school && !formData[openSchoolId]) {
        setFormData((prev) => ({
          ...prev,
          [openSchoolId]: {
            nameOfSchool: school.nameOfSchool || "",
            category: school.category || "",
            location: school.location || "",
            zone: school.zone || "",
            division: school.division || "",
            address: school.address || ""
          }
        }));
      }
    }
  }, [openSchoolId, schools]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setOpenSchoolId(null);
    setShowPrevNext(false);
    setCurrentSchoolIndex(0);
  };

  const handlePreviousSchool = () => {
    if (currentSchoolIndex > 0) {
      const newIndex = currentSchoolIndex - 1;
      setCurrentSchoolIndex(newIndex);
      setOpenSchoolId(filteredSchools[newIndex]._id ?? null);
      const newPage = Math.floor(newIndex / ITEMS_PER_PAGE) + 1;
      setCurrentPage(newPage);
    }
  };

  const handleNextSchool = () => {
    if (currentSchoolIndex < filteredSchools.length - 1) {
      const newIndex = currentSchoolIndex + 1;
      setCurrentSchoolIndex(newIndex);
      setOpenSchoolId(filteredSchools[newIndex]._id ?? null);
      const newPage = Math.floor(newIndex / ITEMS_PER_PAGE) + 1;
      setCurrentPage(newPage);
    }
  };

  const handleToggle = (_id: string | undefined) => {
    if (_id) {
      setOpenSchoolId(openSchoolId === _id ? null : _id);
      setShowPrevNext(openSchoolId !== _id);
      if (openSchoolId !== _id) {
        const index = filteredSchools.findIndex((school) => school._id === _id);
        setCurrentSchoolIndex(index);
        const newPage = Math.floor(index / ITEMS_PER_PAGE) + 1;
        setCurrentPage(newPage);
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleInputChange = (schoolId: string, field: keyof SchoolFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [schoolId]: {
        ...prev[schoolId],
        [field]: value
      }
    }));
    setIsEditing((prev) => ({
      ...prev,
      [schoolId]: true
    }));
  };

  const handleUpdateSchool = async (schoolId: string) => {
    setIsSaving((prev) => ({ ...prev, [schoolId]: true }));

    try {
      // TODO: Replace with your actual API call
      // Example: await updateSchool({ id: schoolId, data: formData[schoolId] });

      const payload = {
        nameOfSchool: formData[schoolId].nameOfSchool,
        category: formData[schoolId].category,
        location: formData[schoolId].location,
        zone: formData[schoolId].zone,
        division: formData[schoolId].division
      };

      console.log("Updating school:", schoolId, "with payload:", payload);

      // Call the actual API
      const result = await updateSchool({
        id: schoolId,
        data: payload
      }).unwrap();

      console.log("Update successful:", result);

      setIsEditing((prev) => ({ ...prev, [schoolId]: false }));
      setUpdateSuccess((prev) => ({ ...prev, [schoolId]: true }));

      // Success message
      alert("✅ School updated successfully!");

      // Clear success message after 5 seconds
      setTimeout(() => {
        setUpdateSuccess((prev) => ({ ...prev, [schoolId]: false }));
      }, 5000);
    } catch (error: any) {
      console.error("Error updating school:", error);

      // Better error handling
      let errorMessage = "Failed to update school. Please try again.";

      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.status === 401) {
        errorMessage = "Unauthorized. Please log in again.";
      } else if (error?.status === 404) {
        errorMessage = "School not found.";
      } else if (error?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      }

      alert(`❌ ${errorMessage}`);
    } finally {
      setIsSaving((prev) => ({ ...prev, [schoolId]: false }));
    }
  };

  const handleCancelEdit = (schoolId: string) => {
    const school = schools.find((s) => s._id === schoolId);
    if (school) {
      setFormData((prev) => ({
        ...prev,
        [schoolId]: {
          nameOfSchool: school.nameOfSchool || "",
          category: school.category || "",
          location: school.location || "",
          zone: school.zone || "",
          division: school.division || "",
          address: school.address || ""
        }
      }));
      setIsEditing((prev) => ({ ...prev, [schoolId]: false }));
    }
  };

  const filteredSchools = schools?.filter((school) => {
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      school.nameOfSchool.toLowerCase().includes(search) ||
      school.category.toLowerCase().includes(search) ||
      school.location.toLowerCase().includes(search) ||
      school?.listOfStaff?.some(
        (staff) =>
          staff?.staffName?.firstName?.toLowerCase().includes(search) ||
          staff?.ogNumber?.toLowerCase().includes(search)
      );

    const matchesZone = !selectedZone || school.zone === selectedZone;

    return matchesSearch && matchesZone;
  });

  const zonesToUppercase = zones.map((zone) => zone.toUpperCase());
  const zoneOptions: SelectOption[] = zonesToUppercase.map((zone) => ({
    value: zone,
    label: zone
  }));
  const division = ["EGBA", "YEWA", "REMO", "IJEBU"];
  //   const divisionToUppercase = zones.map((zone) => zone.toUpperCase());
  const divisionOptions: SelectOption[] = division.map((division) => ({
    value: division,
    label: division
  }));

  // Category options
  const categoryOptions: SelectOption[] = [
    { value: "Combined", label: "Combined" },
    { value: "Senior", label: "Senior" },
    { value: "Junior", label: "Junior" },
    { value: "Zonal Office", label: "Zonal Office" },
    { value: "PG Office", label: "PG Office" }
  ];

  if (isLoading) {
    return <LogoLoader />;
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-500 text-lg">Error loading schools. Please try again.</p>
      </div>
    );
  }

  if (!schools || schools.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500 text-lg">No schools data available.</p>
      </div>
    );
  }

  const totalPages = Math.ceil(filteredSchools.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedSchools = filteredSchools.slice(startIndex, endIndex);

  const handleSelectChange =
    (setter: React.Dispatch<React.SetStateAction<string | null>>) =>
    async (selectedOption: SingleValue<SelectOption>) => {
      const newValue = selectedOption ? selectedOption.value : null;
      setter(newValue);
    };

  return (
    <>
      <Navbar />
      <div className="flex flex-col h-full w-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 mt-16 gap-6 p-6 rounded-xl shadow-xl">
        {/* HEADER ROW */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 w-full">
          {/* LEFT: SCHOOLS COUNT */}
          <div className="flex flex-col gap-1">
            <span className="text-sm uppercase tracking-wide text-white">Update Schools</span>
            <h4 className="text-white text-xl font-semibold">
              Showing
              <span className="mx-2 text-emerald-400 font-bold">{filteredSchools.length}</span>
              of
              <span className="mx-2 text-emerald-400 font-semibold">{schools.length}</span>
              schools, Zonal Offices and PG Offices
            </h4>
          </div>

          {/* MIDDLE: ZONE FILTER */}
          <div className="w-full md:w-[320px]">
            <label htmlFor="zones" className="block text-sm uppercase font-medium text-white mb-2">
              Filter by Zone
            </label>
            <Select
              id="zones"
              options={zoneOptions}
              value={zoneOptions.find((option) => option.value === selectedZone)}
              onChange={handleSelectChange(setSelectedZone)}
              placeholder="Select or search zone"
              isSearchable
              isClearable
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>

          {/* RIGHT: SEARCH */}
          <div className="relative w-full md:w-[420px]">
            <span className="text-sm uppercase tracking-wide text-white">
              Search by Name or OgNumber
            </span>
            <Input
              size="md"
              onChange={handleSearchChange}
              value={searchTerm}
              placeholder="Search schools by name..."
              className="h-11 w-full rounded-full pl-5 pr-12 text-sm bg-white/95 shadow-md focus:ring-2 focus:ring-emerald-500"
            />
            <TesSearch
              size={20}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
            />
          </div>
        </div>

        {paginatedSchools.length > 0 ? (
          paginatedSchools.map((school) => (
            <div
              key={school._id}
              className={`flex flex-col w-full ${
                openSchoolId && openSchoolId !== school._id ? "hidden" : ""
              }`}
            >
              <div
                className="flex flex-row justify-between items-center hover:bg-[#7ed348] hover:text-black bg-[#1b733f] text-white p-3 cursor-pointer rounded-lg shadow-md transition-colors"
                onClick={() => {
                  handleToggle(school._id);
                }}
              >
                <p className="text-lg font-semibold">
                  {school?.nameOfSchool.toUpperCase()} {school?.category.toUpperCase()}{" "}
                  {school?.location.toUpperCase()}
                </p>
                <h5 className="text-xl font-bold">{openSchoolId === school._id ? "-" : "+"}</h5>
              </div>

              <AnimatePresence>
                {openSchoolId === school?._id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden bg-white w-full p-6 rounded-lg shadow-lg mt-2"
                  >
                    <div className="bg-gradient-to-br from-emerald-50 to-white p-6 rounded-lg shadow-md">
                      {/* Header */}
                      <div className="mb-6 pb-4 border-b-2 border-emerald-200">
                        <h2 className="text-2xl font-bold text-emerald-700">
                          Update School Information
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                          Edit the school details below and click Update to save changes
                        </p>
                      </div>

                      {/* Form */}
                      {school._id && formData[school._id] && (
                        <div className="space-y-6">
                          {/* School Name */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              School Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={school._id ? formData[school._id]?.nameOfSchool || "" : ""}
                              onChange={(e) =>
                                school._id &&
                                handleInputChange(school._id, "nameOfSchool", e.target.value)
                              }
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                              placeholder="Enter school name"
                            />
                          </div>

                          {/* Category and Zone - Row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Category */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Category <span className="text-red-500">*</span>
                              </label>
                              <Select
                                options={categoryOptions}
                                value={categoryOptions.find(
                                  (opt) =>
                                    school._id && opt.value === formData[school._id]?.category
                                )}
                                onChange={(option) =>
                                  school._id &&
                                  handleInputChange(school._id, "category", option?.value ?? "")
                                }
                                placeholder="Select category"
                                className="react-select-container"
                                classNamePrefix="react-select"
                              />
                            </div>

                            {/* Zone */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Zone <span className="text-red-500">*</span>
                              </label>
                              <Select
                                options={zoneOptions}
                                value={zoneOptions.find(
                                  (opt) => school._id && opt.value === formData[school._id]?.zone
                                )}
                                onChange={(option) =>
                                  school._id &&
                                  handleInputChange(school._id, "zone", option?.value ?? "")
                                }
                                placeholder="Select zone"
                                className="react-select-container"
                                classNamePrefix="react-select"
                              />
                            </div>
                          </div>

                          {/* Location and Division - Row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Location */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Location <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={school._id ? formData[school._id]?.location || "" : ""}
                                onChange={(e) =>
                                  school._id &&
                                  handleInputChange(school._id, "location", e.target.value)
                                }
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                                placeholder="Enter location"
                              />
                            </div>

                            {/* Division */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Division <span className="text-red-500">*</span>
                              </label>
                              <Select
                                options={divisionOptions}
                                value={divisionOptions.find(
                                  (opt) =>
                                    school._id && opt.value === formData[school._id]?.division
                                )}
                                onChange={(option) =>
                                  school._id &&
                                  handleInputChange(school._id, "division", option?.value ?? "")
                                }
                                placeholder="Select division"
                                className="react-select-container"
                                classNamePrefix="react-select"
                              />
                            </div>
                          </div>

                          {/* Address - Full Width */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Address
                            </label>
                            <textarea
                              value={school._id ? formData[school._id]?.address ?? "" : ""}
                              onChange={(e) =>
                                school._id &&
                                handleInputChange(school._id, "address", e.target.value)
                              }
                              rows={3}
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                              placeholder="Enter full address"
                            />
                          </div>

                          {/* Action Buttons */}
                          <div className="flex justify-end gap-4 pt-4 border-t-2 border-gray-200">
                            <button
                              type="button"
                              onClick={() => school._id && handleCancelEdit(school._id)}
                              disabled={school._id ? isSaving[school._id] : true}
                              className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={async () =>
                                school._id && (await handleUpdateSchool(school._id))
                              }
                              disabled={
                                !school._id || !isEditing[school._id] || isSaving[school._id]
                              }
                              className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {school._id && isSaving[school._id] ? (
                                <>
                                  <svg
                                    className="animate-spin h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Updating...
                                </>
                              ) : (
                                "Update School"
                              )}
                            </button>
                          </div>

                          {/* Change Indicator */}
                          {school._id && isEditing[school._id] && (
                            <div className="mt-2 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                              <p className="text-sm text-yellow-800">
                                <strong>⚠️ Unsaved Changes:</strong> You have made changes to this
                                school. Click "Update School" to save or "Cancel" to discard.
                              </p>
                            </div>
                          )}

                          {/* Success Indicator */}
                          {school._id && updateSuccess[school._id] && (
                            <div className="mt-2 p-3 bg-green-50 border-l-4 border-green-400 rounded animate-pulse">
                              <p className="text-sm text-green-800">
                                <strong>✅ Success:</strong> School information has been updated
                                successfully!
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))
        ) : (
          <p className="text-white text-center">No schools found matching your search.</p>
        )}

        {/* Navigation Buttons */}
        {showPrevNext && (
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={handlePreviousSchool}
              className="px-4 py-2 bg-[#1b733f] text-white rounded shadow-md hover:bg-[#7ed348] hover:text-black transition-colors"
            >
              Previous School
            </button>
            <span className="text-white">
              School {currentSchoolIndex + 1} of {filteredSchools.length}
            </span>
            {currentSchoolIndex < filteredSchools.length - 1 && (
              <button
                onClick={handleNextSchool}
                className="px-4 py-2 bg-[#1b733f] text-white rounded shadow-md hover:bg-[#7ed348] hover:text-black transition-colors"
              >
                Next School
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {!showPrevNext && (
          <div className="flex justify-center mt-4">
            {currentPage !== 1 && (
              <button
                onClick={() => {
                  handlePageChange(currentPage - 1);
                }}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-[#1b733f] text-white rounded-l shadow-md hover:bg-[#7ed348] hover:text-black transition-colors"
              >
                Previous
              </button>
            )}
            <span className="px-4 py-2 bg-[#1b733f] text-white shadow-md">
              Page {currentPage} of {totalPages}
            </span>
            {currentPage !== totalPages && (
              <button
                onClick={() => {
                  handlePageChange(currentPage + 1);
                }}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-[#1b733f] text-white rounded-r shadow-md hover:bg-[#7ed348] hover:text-black transition-colors"
              >
                Next
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
